import {
  AppBskyEmbedImages,
  AppBskyEmbedRecord,
  AppBskyEmbedRecordWithMedia,
  AppBskyFeedDefs,
  AppBskyFeedPost,
} from '@atproto/api'

import {
  type Article,
  type ArticleBlock,
  type ArticleComment,
  type ArticlePreview,
} from '#/features/news/types'
import {NEWS_AUTHOR_HANDLE, NEWS_REQUIRED_TAG} from '#/lib/news/config'

const TITLE_PREFIX_RE = /^(?:TITLE|TITRE)\s*:\s*/i
const REQUIRED_TAG_RE = new RegExp(
  NEWS_REQUIRED_TAG.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  'ig',
)
const REQUIRED_TAG = NEWS_REQUIRED_TAG.toLowerCase()
const AUTHOR_HANDLE = NEWS_AUTHOR_HANDLE.toLowerCase()

export function getPostText(post: AppBskyFeedDefs.PostView): string {
  if (AppBskyFeedPost.isRecord(post.record)) {
    return post.record.text || ''
  }
  return ''
}

export function getPostCreatedAt(
  post: AppBskyFeedDefs.PostView,
): string | undefined {
  if (AppBskyFeedPost.isRecord(post.record)) {
    return post.record.createdAt
  }
  return post.indexedAt || undefined
}

function getPostImages(
  post: AppBskyFeedDefs.PostView,
): AppBskyEmbedImages.ViewImage[] {
  const embed = post.embed
  if (AppBskyEmbedImages.isView(embed)) {
    return embed.images ?? []
  }
  if (AppBskyEmbedRecordWithMedia.isView(embed)) {
    if (AppBskyEmbedImages.isView(embed.media)) {
      return embed.media.images ?? []
    }
  }
  return []
}

function stripRequiredTag(text: string): string {
  // Remove the required tag so it doesn't show up in titles or body text.
  return text.replace(REQUIRED_TAG_RE, '').replace(/[ \t]+$/gm, '').trim()
}

function parseTitleAndSubtitle(rawText: string): {
  title: string
  subtitle?: string
} {
  const lines = rawText
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
  const titleLine = lines[0] ?? ''
  const title = stripRequiredTag(titleLine.replace(TITLE_PREFIX_RE, '')).trim()
  const secondLine = stripRequiredTag(lines[1] ?? '').trim()
  let subtitle = secondLine
  if (!subtitle) {
    const remaining = stripRequiredTag(
      rawText
        .split('\n')
        .slice(1)
        .join('\n'),
    ).trim()
    if (remaining) {
      subtitle = remaining.slice(0, 140)
    }
  }
  return {title, subtitle: subtitle || undefined}
}

export function hasRequiredTag(text: string): boolean {
  return text.toLowerCase().includes(REQUIRED_TAG)
}

function isRootPost(post: AppBskyFeedDefs.PostView): boolean {
  if (!AppBskyFeedPost.isRecord(post.record)) return false
  return !post.record.reply
}

function isQuotePost(post: AppBskyFeedDefs.PostView): boolean {
  return (
    AppBskyEmbedRecord.isView(post.embed) ||
    AppBskyEmbedRecordWithMedia.isView(post.embed)
  )
}

export function isArticleRoot(post: AppBskyFeedDefs.PostView): boolean {
  // Strict filter: author + required tag + root-only (no replies or quotes).
  const text = getPostText(post).trim()
  if (!text) return false
  if (post.author.handle?.toLowerCase() !== AUTHOR_HANDLE) return false
  if (!hasRequiredTag(text)) return false
  if (!isRootPost(post) || isQuotePost(post)) return false
  const {title} = parseTitleAndSubtitle(text)
  return Boolean(title)
}

export function getArticlePreview(
  post: AppBskyFeedDefs.PostView,
  categoryId?: string,
): ArticlePreview | null {
  if (!isArticleRoot(post)) return null
  const {title, subtitle} = parseTitleAndSubtitle(getPostText(post))
  const [cover] = getPostImages(post)
  return {
    uri: post.uri,
    title,
    subtitle,
    cover,
    categoryId,
    publishedAt: getPostCreatedAt(post),
    author: {
      did: post.author.did,
      handle: post.author.handle,
      displayName: post.author.displayName,
      avatar: post.author.avatar,
    },
  }
}

function splitParagraphs(raw: string): string[] {
  // Preserve blank lines as paragraph breaks within each thread post.
  return raw
    .split(/\n\s*\n/)
    .map(section => section.trim())
    .filter(Boolean)
}

function buildBlocksFromPost(
  post: AppBskyFeedDefs.PostView,
  options?: {coverPostUri?: string},
): ArticleBlock[] {
  const raw = stripRequiredTag(getPostText(post)).trim()
  let images = getPostImages(post)
  if (options?.coverPostUri && post.uri === options.coverPostUri) {
    images = images.slice(1)
  }
  if (!raw && images.length === 0) return []

  const paragraphs = splitParagraphs(raw)
  if (paragraphs.length === 0) {
    return [
      {
        type: 'paragraph',
        text: '',
        images,
      },
    ]
  }

  return paragraphs.map((paragraph, index) => ({
    type: 'paragraph',
    text: paragraph,
    images: index === paragraphs.length - 1 ? images : undefined,
  }))
}

function collectComments(
  node:
    | AppBskyFeedDefs.ThreadViewPost
    | AppBskyFeedDefs.BlockedPost
    | AppBskyFeedDefs.NotFoundPost,
  depth: number,
  target: ArticleComment[],
  excludeUris: Set<string>,
) {
  if (!AppBskyFeedDefs.isThreadViewPost(node)) return
  if (excludeUris.has(node.post.uri)) return
  const post = node.post
  const text = getPostText(post).trim()
  const images = getPostImages(post)
  if (text || images.length > 0) {
    target.push({
      uri: post.uri,
      text,
      createdAt: getPostCreatedAt(post),
      depth,
      images,
      author: {
        did: post.author.did,
        handle: post.author.handle,
        displayName: post.author.displayName,
        avatar: post.author.avatar,
      },
    })
  }

  const replies = node.replies ?? []
  for (const reply of replies) {
    collectComments(reply, depth + 1, target, excludeUris)
  }
}

export function parseArticleFromThread(
  thread:
    | AppBskyFeedDefs.ThreadViewPost
    | AppBskyFeedDefs.BlockedPost
    | AppBskyFeedDefs.NotFoundPost,
): {article: Article | null; comments: ArticleComment[]} {
  if (!AppBskyFeedDefs.isThreadViewPost(thread)) {
    return {article: null, comments: []}
  }

  const rootPost = thread.post
  if (!isArticleRoot(rootPost)) {
    return {article: null, comments: []}
  }

  const {title, subtitle} = parseTitleAndSubtitle(getPostText(rootPost))
  const authorDid = rootPost.author.did
  const blocks: ArticleBlock[] = []
  const comments: ArticleComment[] = []
  const chainNodes: AppBskyFeedDefs.ThreadViewPost[] = [thread]

  let current: AppBskyFeedDefs.ThreadViewPost | undefined = thread

  while (current) {
    const replies = current.replies ?? []
    let mainChild: AppBskyFeedDefs.ThreadViewPost | undefined

    for (const reply of replies) {
      if (
        !mainChild &&
        AppBskyFeedDefs.isThreadViewPost(reply) &&
        reply.post.author.did === authorDid
      ) {
        mainChild = reply
      }
    }

    if (mainChild) {
      // Follow the author's first reply chain as the main article body.
      chainNodes.push(mainChild)
    }

    current = mainChild
  }

  const coverNode = chainNodes.find(node => getPostImages(node.post).length > 0)
  const [cover] = coverNode ? getPostImages(coverNode.post) : []

  for (const node of chainNodes) {
    blocks.push(
      ...buildBlocksFromPost(node.post, {
        coverPostUri: coverNode?.post.uri,
      }),
    )
  }

  const chainUris = new Set(chainNodes.map(node => node.post.uri))
  const rootReplies = thread.replies ?? []
  for (const reply of rootReplies) {
    collectComments(reply, 0, comments, chainUris)
  }

  const updatedAt = chainNodes.reduce<string | undefined>((latest, node) => {
    const createdAt = getPostCreatedAt(node.post)
    if (!createdAt) return latest
    if (!latest) return createdAt
    return Date.parse(createdAt) > Date.parse(latest) ? createdAt : latest
  }, getPostCreatedAt(rootPost))

  return {
    article: {
      uri: rootPost.uri,
      title,
      subtitle,
      cover,
      publishedAt: getPostCreatedAt(rootPost),
      updatedAt,
      author: {
        did: rootPost.author.did,
        handle: rootPost.author.handle,
        displayName: rootPost.author.displayName,
        avatar: rootPost.author.avatar,
      },
      blocks,
    },
    comments,
  }
}
